import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import { Column, ViewColumn, ViewColumnUtils } from '../../../types/WorkspaceViewColumn';
import { useGetWorkspace } from '../../../serverState/workspace';
import { WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewRepresentation } from '../../../types/WorkspaceView';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';

export enum ViewColumnComponentType {
  dropdown = 'dropdown',
  newSpaceViewModal = 'new_space_view_modal',
}

export const ViewColumnsGroup: React.FC<{
  componentType: ViewColumnComponentType;
  columns: ViewColumn[];
  updateColumns: (columns: ViewColumn[]) => void;
  updateColumnSelection: (columnId: string, isSelected: boolean) => void;
  representation: WorkspaceViewRepresentation;
}> = ({ componentType, columns, updateColumns, updateColumnSelection, representation }) => {
  const workspaceQuery = useGetWorkspace();
  const framework = workspaceQuery.data?.framework || WorkspaceFramework.none;
  const deselectedItems =
    representation === WorkspaceViewRepresentation.kanban
      ? [Column.sprintStatus, Column.epic, Column.status, Column.estimate]
      : [];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  function onDragStart(event: DragStartEvent) {}

  async function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const indexWhereDropeed = columns.findIndex((p) => p.id === overId);
    const indexFrom = columns.findIndex((p) => p.id === activeId);

    const orderArray = arrayMove(columns, indexFrom, indexWhereDropeed);

    const newColumns = orderArray.map((column, index) => {
      return {
        ...column,
        orderIndex: index,
      };
    });

    updateColumns(newColumns);
  }

  function onDragOver(event: DragOverEvent) {}

  const sortedProperties = columns.sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? a.id));

  const isDisabled = representation === WorkspaceViewRepresentation.kanban;

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
      <CustomTooltip title={isDisabled ? 'Feature coming soon!' : ''}>
        <div className={`relative ${isDisabled ? 'pointer-events-none' : 'pointer-events-auto'}`}>
          <SortableContext items={columns.map((column) => column.id)}>
            {sortedProperties.map((property: ViewColumn) => (
              <ViewPropertySortable
                componentType={componentType}
                key={property.id}
                column={property}
                isSelected={deselectedItems.includes(property.id) ? false : property.selected}
                updatePropertySelection={updateColumnSelection}
                framework={framework}
              />
            ))}
          </SortableContext>

          {isDisabled && (
            <div className="absolute top-0 left-0 w-full h-full bg-white/30 z-10 cursor-pointer pointer-events-auto" />
          )}
        </div>
      </CustomTooltip>
    </DndContext>
  );
};

export const ViewPropertySortable: React.FC<{
  componentType: ViewColumnComponentType;
  column: ViewColumn;
  isSelected: boolean;
  updatePropertySelection: (columnId: string, isSelected: boolean) => void;
  framework: WorkspaceFramework;
}> = ({ componentType, column, isSelected, updatePropertySelection, framework }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: {
      type: 'ViewProperty',
      column,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const handleRowClick = () => {
    updatePropertySelection(column.id, !isSelected);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style }}
      {...attributes}
      {...listeners}
      onClick={handleRowClick}
      className={`flex items-center px-1 py-1.5 cursor-pointer hover:bg-neutral-300 rounded ${componentType === ViewColumnComponentType.newSpaceViewModal ? 'my-0.5' : ''}`}
      key={column.id}
    >
      <HolderOutlined className="mr-2 ml-1" />
      <div className="text-neutral-900 text-sm font-normal leading-tight">
        {ViewColumnUtils.getTitle(column.id, framework)}
      </div>
      <div className="ml-auto" />
      <Switch
        size="small"
        checked={isSelected}
        onChange={(checked) => updatePropertySelection(column.id, checked)}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};
